import { useState } from 'react';
import { confirmMonth, EvidenceSource, TodayStatus, AdminMonth } from '../api/adminApi';
import styles from './ConfirmMonthModal.module.css';

const HIJRI_MONTHS = [
  '', 'Muharram', 'Safar', "Rabi' al-Awwal", "Rabi' al-Thani",
  'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', "Sha'ban",
  'Ramadan', 'Shawwal', "Dhu al-Qi'dah", 'Dhu al-Hijjah',
];

function addDays(gregorianDate: string, days: number): string {
  const d = new Date(gregorianDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function nextMonthInfo(status: TodayStatus): { index: number; name: string; year: number } {
  const cur = status.date.month.index;
  const index = cur === 12 ? 1 : cur + 1;
  const year = cur === 12 ? status.date.year + 1 : status.date.year;
  return { index, name: HIJRI_MONTHS[index] ?? `Month ${index}`, year };
}

interface Props {
  locationCode: string;
  locationName: string;
  status: TodayStatus;
  months: AdminMonth[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function ConfirmMonthModal({ locationCode, locationName, status, onClose, onSuccess }: Props) {
  const [monthLength, setMonthLength] = useState<29 | 30 | null>(null);
  const [sources, setSources] = useState<EvidenceSource[]>([{ url: '', language: 'ar', comment: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const next = nextMonthInfo(status);
  const today = status.gregorianDate;

  const tomorrowDate = today ? addDays(today, 1) : null;
  const dayAfterDate = today ? addDays(today, 2) : null;

  const nextStartDate = monthLength === 29 ? tomorrowDate : monthLength === 30 ? dayAfterDate : null;

  const previewMessage = monthLength === 29
    ? `Tomorrow will become 1 ${next.name} ${next.year}`
    : monthLength === 30
    ? `Tomorrow will become 30 ${status.date.month.en} ${status.date.year}`
    : null;

  function addSource() {
    setSources((s) => [...s, { url: '', language: 'ar', comment: '' }]);
  }

  function removeSource(i: number) {
    setSources((s) => s.filter((_, idx) => idx !== i));
  }

  function updateSource(i: number, field: keyof EvidenceSource, value: string) {
    setSources((s) => s.map((src, idx) => idx === i ? { ...src, [field]: value } : src));
  }

  async function handleSubmit() {
    if (!monthLength) { setError('Please choose whether the month ends at 29 or 30 days.'); return; }
    if (!nextStartDate) { setError('Could not determine the start date for the next month.'); return; }

    setSubmitting(true);
    setError('');
    try {
      const validSources = sources.filter((s) => s.url.trim());
      await confirmMonth({
        locationCode,
        year: next.year,
        month: next.index,
        startDate: nextStartDate,
        source: validSources.length > 0 ? validSources[0].url : 'Manual confirmation',
        sources: validSources.length > 0 ? validSources : undefined,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.title}>Confirm Month End</h2>
            <p className={styles.subtitle}>{locationName} · Day {status.date.day} {status.date.month.en}</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.body}>
          {/* Section 1 — Decision */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>1 — Decision</h3>
            <div className={styles.radioGroup}>
              <label className={`${styles.radioOption} ${monthLength === 29 ? styles.radioSelected : ''}`}>
                <input
                  type="radio"
                  name="monthLength"
                  value="29"
                  checked={monthLength === 29}
                  onChange={() => setMonthLength(29)}
                />
                <div className={styles.radioContent}>
                  <span className={styles.radioLabel}>Month ends at 29 days</span>
                  <span className={styles.radioHint}>Crescent was sighted tonight</span>
                </div>
              </label>
              <label className={`${styles.radioOption} ${monthLength === 30 ? styles.radioSelected : ''}`}>
                <input
                  type="radio"
                  name="monthLength"
                  value="30"
                  checked={monthLength === 30}
                  onChange={() => setMonthLength(30)}
                />
                <div className={styles.radioContent}>
                  <span className={styles.radioLabel}>Month ends at 30 days</span>
                  <span className={styles.radioHint}>Crescent was not sighted tonight</span>
                </div>
              </label>
            </div>
          </section>

          {/* Section 2 — Sources */}
          <section className={styles.section}>
            <div className={styles.sectionHeaderRow}>
              <h3 className={styles.sectionTitle}>2 — Sources / Evidence</h3>
              <button className={styles.addSourceBtn} type="button" onClick={addSource}>+ Add source</button>
            </div>
            <div className={styles.sourcesList}>
              {sources.map((src, i) => (
                <div key={i} className={styles.sourceRow}>
                  <input
                    className={styles.input}
                    type="url"
                    placeholder="https://example.com/announcement"
                    value={src.url}
                    onChange={(e) => updateSource(i, 'url', e.target.value)}
                  />
                  <select
                    className={styles.select}
                    value={src.language}
                    onChange={(e) => updateSource(i, 'language', e.target.value)}
                  >
                    <option value="ar">Arabic</option>
                    <option value="fr">French</option>
                    <option value="en">English</option>
                  </select>
                  <input
                    className={styles.input}
                    placeholder="Comment (optional)"
                    value={src.comment ?? ''}
                    onChange={(e) => updateSource(i, 'comment', e.target.value)}
                  />
                  {sources.length > 1 && (
                    <button className={styles.removeSourceBtn} type="button" onClick={() => removeSource(i)}>✕</button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Section 3 — Preview */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>3 — Preview</h3>
            {previewMessage ? (
              <div className={styles.preview}>
                <span className={styles.previewIcon}>🌙</span>
                <span className={styles.previewText}>{previewMessage}</span>
              </div>
            ) : (
              <div className={styles.previewPlaceholder}>
                Select a decision above to see the preview.
              </div>
            )}
          </section>

          {/* Section 4 — Confirm */}
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>Cancel</button>
            <button
              className={styles.confirmBtn}
              onClick={handleSubmit}
              disabled={!monthLength || submitting}
            >
              {submitting ? 'Confirming…' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
